import NextImage from '@/components/NextImage';
import Image from '@/components/UI/Image';
import { ENGLISH_LANG, rpcMap } from '@/constants';
import {
    areaValidate,
    billValidate,
    cityValidate,
    companyAddressValidate,
    companyNameValidate,
    countryValidate,
    emailValidate,
    firstNameValidate,
    getCountryList,
    getSolutionList,
    HAS_INITIAL_VALUE_SOURCE,
    identityValidate,
    INIT_COUNTRY_MAP,
    lastNameValidate,
    phoneValidate,
    regxEmail,
    regxName,
    regxPhone,
    regxZip,
    solutionValidate,
    zipValidate,
} from '@/constants/quote';
import { createSubscribe, getCitiesList, getCountriesList, getStatesList } from '@/service';
import { submitFreeQuote, submitFreeQuoteToB, subscribeToB } from '@/service/quote';
import { useGlobal } from '@/utils/useGlobal';
import { Button, Checkbox, Form, Input, message, Select } from 'antd';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import css from './QuoteModal.module.scss';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';

const OTHER_COUNTRY = ['Other EU countries', 'Other countries'];
const option_map_1 = {
    site_dealer_companyType_item1: 'Distributor',
    site_dealer_companyType_item2: 'Installer',
    site_dealer_companyType_item3: 'Electrician',
    site_dealer_companyType_item4: 'Homeowner',
    site_dealer_companyType_item5: 'Consultant',
    site_dealer_companyType_item6: 'Media',
    site_dealer_companyType_item7: 'Wholesaler',
    site_dealer_companyType_item8: 'Retailer',
    site_dealer_companyType_item9: 'Others',
};

const option_map_2 = {
    site_dealer_product_interests_item1: 'Home solar battery solution',
    site_dealer_product_interests_item2: 'Power stations',
    site_dealer_product_interests_item3: 'Balcony power plants',
    site_dealer_product_interests_item4: 'Solar panels',
    site_dealer_product_interests_item5: 'Smart devices',
    site_dealer_product_interests_item6: 'Others',
};

export default function QuoteModal({ callback, initType, langKey, fromSource = '', handleQuoteJump }: any) {
    const { useLang, useRouter, isMobile, gtm, Link } = useGlobal();
    const { t } = useLang('shared.product.naver_quote_modal');
    const { t: t1 } = useLang('shared');
    const { locale } = useRouter();
    const [countryList, setCountryList] = useState([]);
    const solutionList = getSolutionList(locale);
    const [type, setType] = useState(initType || 0); // 0 未选择状态 1 houseOwner 2 partner
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [otherCountry, setOtherCountry] = useState(false); // 是否选中Other EU countries
    // 提交按钮
    const [canSubmit, setCanSubmit] = useState(false);
    // 提交后 loading
    const [isLoading, setIsLoading] = useState(false);
    const [subscribe, setSubscribe] = useState(false);
    const recaptchaRef = useRef();
    const waitTimer = useRef<any>(null);

    const [siteKey, setSiteKey] = useState(process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY || '');
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const [requested, setRequested] = useState(0);
    // B端更新
    const [countries, setCountries] = useState<any>([]);
    const [states, setStates] = useState<any>([]);
    const [cities, setCities] = useState<any>([]);
    const [showCompanyTypeSpecify, setShowCompanyTypeSpecify] = useState(false);
    const [prefixCode, setPrefixCode] = useState('');
    // form 实例
    const [form] = Form.useForm();
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const [showSolarInfo, setShowSolarInfo] = useState<any>(false);

    const [hasRegion, setHasRegion] = useState(false);
    const [hasBar, setHasBar] = useState(false);

    const OTHER_COUNTRY2 = [
        {
            key: 404,
            label: t('other_country'),
            value: t('other_country'),
            phoneCode: '',
        },
    ];
    const OTHER_STATE = [
        {
            key: 404,
            value: t('other_state'),
            label: t('other_state'),
        },
    ];
    const OTHER_CITY = [
        {
            key: 404,
            value: t('other_city'),
            label: t('other_city'),
        },
    ];

    const linkComp = useMemo(() => {
        if (locale === 'de') {
            return t.rich('subscription1', {
                link: (children: string) => (
                    <Link href="/de/home-battery/financing" className={'subscription-link'} newWin>
                        {children}
                    </Link>
                ),
            });
        }
        return <>{t('subscription')}</>;
    }, [locale, t]);

    const onFinish = async () => {
        setIsLoading(true);
        const data: any = form.getFieldsValue();
        message.config({ top: isMobile ? 80 : 120 });
        try {
            // 订阅邮箱
            if (subscribe) {
                if (type === 1) {
                    await createSubscribe({
                        email: data.email,
                        source: 'WEB_CREDITS',
                        ...subscribeCookie({
                            sector_name: type === 2 ? t('title_partner') : t('title'),
                            entry_position: 1,
                        }),
                    });
                } else if (type === 2) {
                    await subscribeToB(
                        {
                            email: data.email,
                            source: 'PARTNER_PROPOSAL',
                            ...subscribeCookie({
                                sector_name: type === 2 ? t('title_partner') : t('title'),
                                entry_position: 1,
                            }),
                        },
                        locale,
                    );
                }
            }

            let res: any;
            if (type === 1) {
                data.optIn = subscribe;
                res = await submitFreeQuote(data, locale);
            } else if (type === 2) {
                if (data.identity === option_map_1.site_dealer_companyType_item9 && data.companyTypeSpecify) {
                    data.identity = data.companyTypeSpecify;
                }
                data.phoneNumber = prefixCode + data.phoneNumber;
                data.optIn = subscribe;
                if (!data.interestedProductCategory) {
                    data.interestedProductCategory = option_map_2.site_dealer_product_interests_item1;
                }
                res = await submitFreeQuoteToB(data, locale);
            }
            if (res?.code === '0') {
                message.success(t('success'));
                setIsLoading(false);

                // 埋点
                try {
                    gtm?.push({
                        event: 'uaEvent',
                        event_name: 'pre_order_submit',
                        button_name: type === 1 ? 'houseowner_submit' : 'business partner_submit',
                        status: 'success',
                    });
                } catch (e) {
                    console.error(e);
                }
            } else {
                message.error(res?.message || res?.statusText);
                // 埋点
                try {
                    gtm?.push({
                        event: 'uaEvent',
                        event_name: 'pre_order_submit',
                        button_name: type === 1 ? 'houseowner_submit' : 'business partner_submit',
                        status: 'fail',
                    });
                } catch (e) {
                    console.error(e);
                }
            }
        } catch (err) {
            console.error(err);
            message.error(err?.message);
            setIsLoading(false);

            // 埋点
            try {
                gtm?.push({
                    event: 'uaEvent',
                    event_name: 'pre_order_submit',
                    button_name: type === 1 ? 'houseowner_submit' : 'business partner_submit',
                    status: 'fail',
                });
            } catch (e) {
                console.error(e);
            }
        } finally {
            setRequested(requested + 1);
            callback();
            setIsLoading(false);
        }
    };

    const onValuesChange = async () => {
        const formData = form.getFieldsValue();
        const {
            firstName = '',
            lastName = '',
            country = '',
            zipCode = '',
            postalCode = '',
            email = '',
            phone = '',
            phoneNumber = '',
            companyName = '',
            companyAddress = '',
            customerSituation = '',
            identity = '',
            message = '',
            recaptchaResponse = '',
            otherCountry = '',
            city = '',
            state = '',
            electricBill = '',
            houseArea = '',
        } = formData;
        if (type === 1) {
            const allReauireTrue =
                firstName?.trim() !== '' &&
                firstName?.length <= 30 &&
                regxName.test(firstName) &&
                lastName?.trim() !== '' &&
                lastName?.length <= 30 &&
                regxName.test(lastName) &&
                country?.trim() !== '' &&
                ((OTHER_COUNTRY.includes(country) && otherCountry?.length > 0) || !OTHER_COUNTRY.includes(country)) &&
                city?.trim() !== '' &&
                ((zipCode?.length <= 30 && zipCode?.length > 0 && regxZip.test(zipCode)) || !zipCode) &&
                email?.trim() !== '' &&
                regxEmail.test(email) &&
                email.length <= 100 &&
                electricBill?.trim() !== '' &&
                houseArea?.trim() !== '' &&
                !!phone &&
                phone.length >= 7 &&
                phone.length <= 30 &&
                regxPhone.test(phone) &&
                customerSituation?.trim() !== '' &&
                (!message || message.length <= 2000) &&
                recaptchaResponse?.trim() !== '';
            return setCanSubmit(allReauireTrue);
        }
        if (type === 2) {
            const allReauireTrue =
                firstName?.trim() !== '' &&
                firstName?.length <= 30 &&
                regxName.test(firstName) &&
                lastName?.trim() !== '' &&
                lastName?.length <= 30 &&
                regxName.test(lastName) &&
                country?.trim() !== '' &&
                state?.trim() !== '' &&
                city?.trim() !== '' &&
                postalCode?.length <= 30 &&
                postalCode?.length > 0 &&
                regxZip.test(postalCode) &&
                identity?.trim() !== '' &&
                companyName?.trim() !== '' &&
                companyName?.length <= 200 &&
                companyAddress?.trim() !== '' &&
                companyAddress?.length <= 300 &&
                email?.trim() !== '' &&
                regxEmail.test(email) &&
                email.length <= 100 &&
                !!phoneNumber &&
                phoneNumber.length >= 7 &&
                phoneNumber.length <= 30 &&
                regxPhone.test(phoneNumber) &&
                (!message || message.length <= 2000) &&
                recaptchaResponse?.trim() !== '';
            return setCanSubmit(allReauireTrue);
        }
    };
    const onCountryChange = (item: any) => {
        setOtherCountry(Boolean(OTHER_COUNTRY.includes(item)));
    };
    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) {
            return;
        }
        setRecaptchaResponse(captchaCode);
        form.setFieldsValue({ recaptchaResponse: captchaCode });
        onValuesChange();
    };

    const renderRecaptcha = useMemo(() => {
        return (
            <div className="g-recaptcha" key={Math.random() + new Date().getTime()}>
                {/* @ts-ignore */}
                <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={siteKey} onChange={onReCAPTCHAChange} />
            </div>
        );
    }, [requested, type, siteKey, recaptchaRef]);

    const onClose = () => {
        callback();
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'pre_order_close',
                button_name: type === 1 ? 'houseowner_close' : 'business partner_close',
            });
        } catch (err) {
            console.error(err);
        }
    };

    const getCity = async (id: number) => {
        try {
            const res: any = await getCitiesList({
                stateId: id,
                page: 1,
                size: 3000,
            });

            if (res.code === '0') {
                const cityList: any = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    label: item.name,
                    value: item.name,
                }));
                setCities(cityList.concat(OTHER_CITY));
            } else {
                message.error(res?.message);
            }
            // if (id === 404) {
            //     setCities(OTHER_CITY);
            // }
        } catch (err) {
            console.log(err);
        }
    };

    const getStates = async (id: number) => {
        try {
            const res: any = await getStatesList({
                countryId: id,
                page: 1,
                size: 300,
            });
            if (res.code === '0') {
                const stateList = res.data?.content?.map((item: any) => ({
                    key: item.id,
                    value: item.name,
                    label: item.name,
                }));
                setStates(stateList.concat(OTHER_STATE));
            } else {
                message.error(res?.message);
            }
            // if (id === 404) {
            //     setStates(OTHER_STATE);
            // }
        } catch (err) {
            console.log(err);
        }
    };

    const onStateChange = (_val: any, option: any) => {
        form.setFieldsValue({
            city: undefined,
        });
        getCity(option.key);
    };

    const onCountryChange2 = (_val: any, option: any) => {
        // select组件重置值，也会把placeholder清除，所以提前获取值判断是否存在值
        form.setFieldsValue({
            state: undefined,
        });
        form.setFieldsValue({
            city: undefined,
        });
        getStates(option.key);
        const target: any = countries.filter((item: any) => item.key === Number(option.key));
        setPrefixCode(target[0]?.phoneCode);
        setCities([]);
    };

    const getCountry = async () => {
        const firstCountries = [
            {
                key: 82,
                label: 'Germany',
                value: 'Germany',
                phoneCode: '+49',
            },
            {
                key: 15,
                label: 'Austria',
                value: 'Austria',
                phoneCode: '+43',
            },
            {
                key: 214,
                label: 'Switzerland',
                value: 'Switzerland',
                phoneCode: '+41',
            },
        ];
        // 因国家/省/市列表只有英文的，所以暂时写死语言为 us
        const result: any = await getCountriesList(
            {
                page: 1,
                size: 300,
            },
            'us',
        );
        if (result?.code === '0') {
            const countryList: any = [];
            result.data?.content?.forEach((item: any) => {
                if (!['Germany', 'Austria', 'Switzerland'].includes(item.name)) {
                    countryList.push({
                        key: item.id,
                        label: item.name,
                        value: item.name,
                        phoneCode: item.phoneCode,
                    });
                }
            });
            setCountries(firstCountries.concat(countryList, OTHER_COUNTRY2));
        }
    };

    const companyTypeOption1 = [
        {
            label: t('site_dealer_companyType_item1'),
            value: option_map_1.site_dealer_companyType_item1,
        },
        {
            label: t('site_dealer_companyType_item2'),
            value: option_map_1.site_dealer_companyType_item2,
        },
        {
            label: t('site_dealer_companyType_item3'),
            value: option_map_1.site_dealer_companyType_item3,
        },
        {
            label: t('site_dealer_companyType_item4'),
            value: option_map_1.site_dealer_companyType_item4,
        },
        {
            label: t('site_dealer_companyType_item5'),
            value: option_map_1.site_dealer_companyType_item5,
        },
        {
            label: t('site_dealer_companyType_item6'),
            value: option_map_1.site_dealer_companyType_item6,
        },
        {
            label: t('site_dealer_companyType_item7'),
            value: option_map_1.site_dealer_companyType_item7,
        },
        {
            label: t('site_dealer_companyType_item9'),
            value: option_map_1.site_dealer_companyType_item9,
        },
    ];
    const companyTypeOption2 = [
        {
            label: t('site_dealer_companyType_item1'),
            value: option_map_1.site_dealer_companyType_item1,
        },
        {
            label: t('site_dealer_companyType_item7'),
            value: option_map_1.site_dealer_companyType_item7,
        },
        {
            label: t('site_dealer_companyType_item8'),
            value: option_map_1.site_dealer_companyType_item8,
        },
        {
            label: t('site_dealer_companyType_item9'),
            value: option_map_1.site_dealer_companyType_item9,
        },
    ];
    console.log({ companyTypeOption1 });
    const [companyTypeOption, setCompanyTypeOption] = useState(companyTypeOption1);

    const productInterestsOption = [
        {
            label: t('site_dealer_product_interests_item1'),
            value: option_map_2.site_dealer_product_interests_item1,
        },
        {
            label: t('site_dealer_product_interests_item2'),
            value: option_map_2.site_dealer_product_interests_item2,
        },
        {
            label: t('site_dealer_product_interests_item3'),
            value: option_map_2.site_dealer_product_interests_item3,
        },
        {
            label: t('site_dealer_product_interests_item4'),
            value: option_map_2.site_dealer_product_interests_item4,
        },
        {
            label: t('site_dealer_product_interests_item5'),
            value: option_map_2.site_dealer_product_interests_item5,
        },
        {
            label: t('site_dealer_product_interests_item6'),
            value: option_map_2.site_dealer_product_interests_item6,
        },
    ];

    const Arrow = () => (
        <svg width={isMobile ? '12' : '20'} height={isMobile ? '12' : '20'} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fill-rule="evenodd">
                <path d="M0 0h16v16H0z" />
                <path
                    d="m9.226 8-4.195 4.233a.775.775 0 0 0-.231.567c0 .222.077.411.231.567a.761.761 0 0 0 .562.233c.22 0 .407-.078.561-.233l4.79-4.834c.352-.355.341-.722-.033-1.1l-4.757-4.8a.761.761 0 0 0-.561-.233.761.761 0 0 0-.562.233.775.775 0 0 0-.231.567c0 .222.077.411.231.567L9.226 8z"
                    fill="#03060B"
                />
            </g>
        </svg>
    );

    const onProductInterestsChange = (item: any) => {
        setCompanyTypeOption(item === option_map_2.site_dealer_product_interests_item1 ? companyTypeOption1 : companyTypeOption2);
        form.setFieldsValue({
            identity: undefined,
        });
    };

    const onCompanyTypeChange = (item: any) => setShowCompanyTypeSpecify(item === option_map_1.site_dealer_companyType_item9);

    const onSearch = (value: string) => value.slice(0, 1).toUpperCase() + value.slice(1);

    const handleInitCountry = () => {
        form.setFieldsValue({ country: INIT_COUNTRY_MAP[locale as keyof typeof INIT_COUNTRY_MAP]?.name || 'Germany' });
        getStates(INIT_COUNTRY_MAP[locale as keyof typeof INIT_COUNTRY_MAP]?.id || 82);
    };

    const handleBatterySolutionChange = (value: any) => {
        if (value === 'my house with an existing solar system') {
            setShowSolarInfo(true);
        } else {
            setShowSolarInfo(false);
        }
    };

    useEffect(() => {
        if (!isMobile && typeof window !== 'undefined') {
            const target = document.querySelector('.naver-quote-modal .modal-wrapper') as HTMLDivElement;
            setIsSmallScreen(target.offsetHeight >= window.innerHeight - 160);
        }
    }, [isMobile, type]);

    useEffect(() => {
        type === 1
            ? setSiteKey(process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY || '')
            : setSiteKey(process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY_TO_B || '');

        if (type === 1) {
            setCountryList(getCountryList(locale, type));
        }
    }, [type]);

    useEffect(() => {
        getCountry();

        const regionPopupEl = document.querySelector('.regionPopup') as HTMLDivElement;
        setHasRegion(!!regionPopupEl);

        const chrismasBarEl = document.querySelector('.chrismas-new-year-bar') as HTMLDivElement;
        setHasBar(!!chrismasBarEl);

        if (!!HAS_INITIAL_VALUE_SOURCE.includes(fromSource)) {
            form.setFieldsValue({ customerSituation: 'my house without any solar system' });
        }
        waitTimer.current = setTimeout(() => {
            handleInitCountry();
        }, 300);
    }, []);

    return (
        <div
            className={`naver-quote-modal ${css[`naver-quote-modal-${type}`]} ${css['naver-quote-modal']} ${isMobile ? 'modal-h5' : 'modal-pc'} ${
                hasRegion ? css['quote-modal-region'] : ''
            } ${hasBar ? css['quote-modal-bar'] : ''}`}
        >
            {/* {contextHolder} */}
            <div className={`modal-wrapper ${isSmallScreen ? '' : 'normal'}`}>
                <div className="close-box">
                    <span className="close" onClick={onClose}>
                        &times;
                    </span>
                </div>
                <div className="modal-content">
                    <p className={`title`}>{type === 2 ? t('title_partner') : t('title')}</p>
                    <p className={`desc`}>{type === 0 ? t('desc_type') : type === 1 ? t('desc') : t('desc_partner')}</p>
                    {type === 0 && (
                        <div className="init-box">
                            <div
                                className="img-box"
                                onClick={() => {
                                    if (handleQuoteJump) {
                                        handleQuoteJump();
                                    } else {
                                        setType(1);
                                    }

                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'pop_ups',
                                        title_name: 'houseowner',
                                        button_name: 'Click Here',
                                    });
                                }}
                            >
                                <NextImage
                                    src={require(`@publicLocal/power-ocean/home/modal/1${isMobile ? '_h5' : ''}.jpg`)}
                                    className="fullImgs"
                                    aria-lable={t('type_houseowner')}
                                    alt={t('type_houseowner')}
                                />
                                <div className="text-container">
                                    <p className="img-title">{t('type_houseowner')}</p>
                                    <div className="img-btn">{t('type_btn')}</div>
                                </div>
                            </div>
                            <div
                                className="img-box"
                                onClick={() => {
                                    setType(2);
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'pop_ups',
                                        title_name: 'business partner',
                                        button_name: 'Click Here',
                                    });
                                }}
                            >
                                <NextImage
                                    src={require(`@publicLocal/power-ocean/home/modal/2${isMobile ? '_h5' : ''}.jpg`)}
                                    className="fullImgs"
                                    aria-lable={t('type_partner')}
                                    alt={t('type_partner')}
                                />
                                <div className="text-container">
                                    <p className="img-title">{t('type_partner')}</p>
                                    <div className="img-btn">{t('type_btn')}</div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="form-area">
                        {type === 1 && (
                            <Form name="quote" form={form} layout="vertical" onFinish={onFinish} onValuesChange={debounce(onValuesChange, 500)}>
                                <div className="columns">
                                    <Form.Item name="firstName" required rules={[firstNameValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('firstname')}
                                            aria-label={t('firstname')}
                                            alt={t('firstname')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                    <Form.Item name="lastName" required rules={[lastNameValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('lastname')}
                                            aria-label={t('lastname')}
                                            alt={t('lastname')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="country" required rules={[countryValidate.bind(null, t)]}>
                                        <Select
                                            className="ef-comp-selector"
                                            placeholder={t('country')}
                                            onChange={(item) => onCountryChange(item)}
                                            suffixIcon={<Arrow />}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                        >
                                            {countryList?.map((item: any) => (
                                                <Select.Option value={item.text} key={item.key}>
                                                    {item?.textAlias || item.text}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    {otherCountry && (
                                        <Form.Item name="otherCountry" required rules={[countryValidate.bind(null, t)]}>
                                            <Input
                                                className="default-input vertical-line"
                                                placeholder={t('otherCountry')}
                                                alt={t('otherCountry')}
                                                aria-label={t('otherCountry')}
                                                maxLength={100}
                                            />
                                        </Form.Item>
                                    )}
                                </div>
                                <div className="columns">
                                    <Form.Item name="city" required rules={[cityValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input vertical-line"
                                            placeholder={t('city')}
                                            alt={t('city')}
                                            aria-label={t('city')}
                                            maxLength={100}
                                        />
                                    </Form.Item>
                                    <Form.Item name="zipCode" rules={[zipValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input vertical-line"
                                            placeholder={t('zip')}
                                            alt={t('zip')}
                                            aria-label={t('zip')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="email" required rules={[emailValidate.bind(null, t)]}>
                                        <Input className="default-input" placeholder={t('email')} aria-label={t('email')} alt={t('email')} maxLength={100} />
                                    </Form.Item>
                                    <Form.Item name="phone" required rules={[phoneValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('site_dealer_table_phone')}
                                            aria-label={t('site_dealer_table_phone')}
                                            alt={t('site_dealer_table_phone')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                </div>
                                {type === 1 && (
                                    <div className="columns scale-small">
                                        <Form.Item name="electricBill" required rules={[billValidate.bind(null, t)]}>
                                            <Input.TextArea
                                                className="default-input"
                                                placeholder={`${t('bill_empty')}*`}
                                                aria-label={t('bill_empty')}
                                                maxLength={1000}
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item name="houseArea" rules={[areaValidate.bind(null, t)]}>
                                            <Input.TextArea
                                                className="default-input"
                                                placeholder={`${t('area_empty')}*`}
                                                aria-label={t('area_empty')}
                                                maxLength={1000}
                                                allowClear
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                                <div className="columns last mini-scale">
                                    <Form.Item name="customerSituation" required rules={[solutionValidate.bind(null, t)]}>
                                        <Select
                                            className="ef-comp-selector"
                                            placeholder={t('solutions')}
                                            suffixIcon={<Arrow />}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            onChange={handleBatterySolutionChange}
                                        >
                                            {solutionList.map((item: any) => (
                                                <Select.Option value={item.text} key={item.key}>
                                                    {item?.aliaText || item.text}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </div>
                                {!!showSolarInfo && (
                                    <div className={`columns last ${!!showSolarInfo ? 'inverter-column' : ''}`}>
                                        <Form.Item
                                            name="inverterBrand"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: <>{t('inverter-brand-placeholder')}</>,
                                                },
                                            ]}
                                        >
                                            <Input.TextArea className="default-input" placeholder={t('inverter-brand-placeholder')} maxLength={100} />
                                        </Form.Item>
                                        <Form.Item
                                            name="inverterModel"
                                            style={!!showSolarInfo && !!isMobile && locale === 'de' ? { marginTop: 40 } : {}}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: <>{t('inverter-model-placeholder')}</>,
                                                },
                                            ]}
                                        >
                                            <Input.TextArea className="default-input" placeholder={t('inverter-model-placeholder')} maxLength={100} />
                                        </Form.Item>
                                    </div>
                                )}
                                <div
                                    className={`columns last`}
                                    style={
                                        !!showSolarInfo && !!isMobile && locale === 'de'
                                            ? { marginTop: 40 }
                                            : !!showSolarInfo && !isMobile
                                            ? { marginTop: 20 }
                                            : {}
                                    }
                                >
                                    <Form.Item name="message">
                                        <Input.TextArea maxLength={2000} rows={4} showCount placeholder={t('message')} aria-label={t('message')} />
                                    </Form.Item>
                                </div>
                                <div className="columns memo">{t('memo')}</div>
                                {locale === 'de' ? (
                                    <p style={{ textAlign: 'left', marginBottom: '20px' }}>{linkComp}</p>
                                ) : (
                                    <Checkbox onChange={(e) => setSubscribe(e.target.checked)}>{t('subscription')}</Checkbox>
                                )}
                                <div className="columns last recha">
                                    <Form.Item
                                        name="recaptchaResponse"
                                        rules={[
                                            {
                                                validator() {
                                                    return recaptchaResponse
                                                        ? Promise.resolve()
                                                        : Promise.reject(t1('logintips.site_login_tip_perform_verification'));
                                                },
                                            },
                                        ]}
                                        style={{ marginBottom: isMobile ? '.1rem' : '32px' }}
                                    >
                                        {renderRecaptcha}
                                    </Form.Item>
                                </div>
                                <div className="sub-btn">
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            className={`submit-btn`}
                                            disabled={!canSubmit}
                                            loading={isLoading}
                                            aria-label={t('submit')}
                                        >
                                            {t('submit')}
                                        </Button>
                                    </Form.Item>
                                </div>
                            </Form>
                        )}

                        {type === 2 && (
                            <Form name="quote" form={form} layout="vertical" onFinish={onFinish} onValuesChange={debounce(onValuesChange, 500)}>
                                <div className="columns">
                                    <Form.Item name="firstName" required rules={[firstNameValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('firstname')}
                                            aria-label={t('firstname')}
                                            alt={t('firstname')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                    <Form.Item name="lastName" required rules={[lastNameValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('lastname')}
                                            aria-label={t('lastname')}
                                            alt={t('lastname')}
                                            maxLength={30}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="country" required>
                                        <Select
                                            className="ef-comp-selector"
                                            showSearch
                                            onSearch={onSearch}
                                            placeholder={t('site_dealer_table_country')}
                                            onChange={onCountryChange2}
                                            suffixIcon={<Arrow />}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            options={countries}
                                        />
                                    </Form.Item>
                                    <Form.Item name="state" required>
                                        <Select
                                            className="ef-comp-selector"
                                            onChange={onStateChange}
                                            placeholder={t('site_dealer_table_state')}
                                            showSearch
                                            onSearch={onSearch}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            suffixIcon={<Arrow />}
                                            options={states}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="city" required>
                                        <Select
                                            className="ef-comp-selector"
                                            placeholder={t('site_dealer_table_city')}
                                            onChange={(item) => onCountryChange(item)}
                                            showSearch
                                            onSearch={onSearch}
                                            suffixIcon={<Arrow />}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            options={cities}
                                        />
                                    </Form.Item>
                                    <Form.Item name="postalCode" required rules={[zipValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('site_dealer_table_postal_zip_code')}
                                            aria-label={t('site_dealer_table_postal_zip_code')}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="phoneNumber" required rules={[phoneValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('site_dealer_table_phone')}
                                            aria-label={t('site_dealer_table_phone')}
                                            alt={t('site_dealer_table_phone')}
                                            maxLength={30}
                                            prefix={
                                                prefixCode && (
                                                    <>
                                                        <span>{prefixCode}</span>
                                                        <div className="divider"></div>
                                                    </>
                                                )
                                            }
                                        />
                                    </Form.Item>
                                    <Form.Item name="email" required rules={[emailValidate.bind(null, t)]}>
                                        <Input className="default-input" placeholder={t('email')} aria-label={t('email')} alt={t('email')} maxLength={100} />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="companyName" required rules={[companyNameValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('company_name')}
                                            aria-label={t('company_name')}
                                            alt={t('company_name')}
                                            maxLength={200}
                                        />
                                    </Form.Item>
                                    <Form.Item name="interestedProductCategory" required>
                                        <Select
                                            className="ef-comp-selector"
                                            placeholder={t('site_dealer_product_interests')}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            suffixIcon={<Arrow />}
                                            defaultValue={option_map_2.site_dealer_product_interests_item1}
                                            dropdownClassName="ef-select-item"
                                            options={productInterestsOption}
                                            onChange={(item) => onProductInterestsChange(item)}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns">
                                    <Form.Item name="identity" required rules={[identityValidate.bind(null, t)]}>
                                        <Select
                                            className="ef-comp-selector"
                                            placeholder={t('identity')}
                                            suffixIcon={<Arrow />}
                                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            onChange={(item) => onCompanyTypeChange(item)}
                                            options={companyTypeOption}
                                        />
                                    </Form.Item>
                                    {showCompanyTypeSpecify && (
                                        <Form.Item name="companyTypeSpecify">
                                            <Input
                                                className="default-input full-width"
                                                placeholder={t('site_dealer_please_specify')}
                                                aria-label={t('site_dealer_please_specify')}
                                                maxLength={49}
                                            />
                                        </Form.Item>
                                    )}
                                </div>
                                <div className="columns last">
                                    <Form.Item
                                        name="companyWebsite"
                                        rules={[
                                            {
                                                validator(_: any, value: any) {
                                                    const regx = /^((https|http|ftp|rtsp|mms){0,1}(:\/\/){0,1})www\.(([A-Za-z0-9-~]+)\.)+([A-Za-z0-9-~\/])+$/;
                                                    if (value && !regx.test(value)) {
                                                        return Promise.reject(new Error(t1('user.site_account_address_company_website_wrong_tip')));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Input
                                            className="default-input full-width"
                                            placeholder={t('site_dealer_table_company_website')}
                                            aria-label={t('site_dealer_table_company_website')}
                                            maxLength={150}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns last">
                                    <Form.Item name="companyAddress" required rules={[companyAddressValidate.bind(null, t)]}>
                                        <Input
                                            className="default-input"
                                            placeholder={t('site_dealer_table_company_address')}
                                            aria-label={t('site_dealer_table_company_address')}
                                            alt={t('site_dealer_table_company_address')}
                                            maxLength={300}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns last">
                                    <Form.Item name="addressLineTwo">
                                        <Input
                                            className="default-input full-width"
                                            placeholder={t('site_dealer_table_address_line2')}
                                            aria-label={t('site_dealer_table_address_line2')}
                                            maxLength={255}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="columns last">
                                    <Form.Item name="message">
                                        <Input.TextArea maxLength={2000} rows={4} showCount placeholder={t('message')} aria-label={t('message')} />
                                    </Form.Item>
                                </div>
                                <Checkbox onChange={(e) => setSubscribe(e.target.checked)}>{t('subscription')}</Checkbox>
                                <div className="columns last recha">
                                    <Form.Item
                                        name="recaptchaResponse"
                                        rules={[
                                            {
                                                validator() {
                                                    return recaptchaResponse
                                                        ? Promise.resolve()
                                                        : Promise.reject(t1('logintips.site_login_tip_perform_verification'));
                                                },
                                            },
                                        ]}
                                        style={{ marginBottom: isMobile ? '.1rem' : '32px' }}
                                    >
                                        {renderRecaptcha}
                                    </Form.Item>
                                </div>
                                <div className="sub-btn">
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            className={`submit-btn`}
                                            disabled={!canSubmit}
                                            loading={isLoading}
                                            aria-label={t('submit')}
                                        >
                                            {t('submit')}
                                        </Button>
                                    </Form.Item>
                                </div>
                            </Form>
                        )}
                    </div>
                </div>
            </div>
            <style jsx global>
                {`
                    .ecoflow-frame-main {
                        z-index: ${langKey !== 'solarspeicher' ? '1101 !important' : '1'};
                    }
                    .ecoflow-mobile {
                        height: ${langKey !== 'solarspeicher' ? '100vh' : 'auto'};
                        overflow: ${langKey !== 'solarspeicher' ? 'hidden' : 'auto'};
                    }
                    .ecoflow-pc {
                        height: ${langKey !== 'solarspeicher' ? '100vh' : 'auto'};
                        overflow: ${langKey !== 'solarspeicher' ? 'hidden' : 'auto'};
                    }
                `}
            </style>
        </div>
    );
}
